import { useEffect, useRef, useState } from "react";
import { Filter as FilterIcon, Check } from "lucide-react";
import { getCsrfToken } from "../lib/auth";

/** Reusable filter popover. Surfaces in the corner of any view that
 *  serves a list of user-scoped rows (Today snapshots, People,
 *  Recall list, captures, etc.). Lets the user toggle privacy
 *  scopes + a "Show test entries" switch and remembers the choice
 *  per-view via user_settings.view_filters[viewKey].
 *
 *  Defaults: all scopes ON except `private` (which is always opt-in
 *  because private content is sensitive). is_test OFF by default so
 *  routine views aren't polluted by dev-test rows.
 *
 *  The component is intentionally agnostic of which scopes the
 *  backend can apply: a list endpoint that doesn't have a scope
 *  column (e.g. /api/people) ignores the scope filter; one that
 *  does (/api/captures, /api/conversation/list) applies it. The UI
 *  stays consistent so the user has one mental model. */

// Canonical 6-tag model (Step 1.5). `social` kept last for legacy
// row/back-compat type-checking; new data never uses it.
export const ALL_SCOPES = ["personal", "work", "family", "financial", "business", "private", "social"] as const;
export type Scope = typeof ALL_SCOPES[number];

export interface ViewFilterState {
  scopes: Scope[];
  includeTest: boolean;
}

const DEFAULT_STATE: ViewFilterState = {
  scopes: ["work", "business", "personal", "family", "social"],
  includeTest: false,
};

/** Read a previously-saved filter state for a view key. Falls back to
 *  the default when the user hasn't customized this view yet, OR when
 *  the saved value is malformed. */
async function readSavedFilter(viewKey: string): Promise<ViewFilterState> {
  try {
    const r = await fetch(`/api/user/settings/view_filters`, { credentials: "same-origin" });
    if (r.status === 404) return DEFAULT_STATE;
    if (!r.ok) return DEFAULT_STATE;
    const body = (await r.json()) as { value?: Record<string, unknown> };
    const all = body.value || {};
    const raw = (all as Record<string, unknown>)[viewKey];
    if (!raw || typeof raw !== "object") return DEFAULT_STATE;
    const v = raw as { scopes?: unknown; includeTest?: unknown };
    const scopes = Array.isArray(v.scopes)
      ? (v.scopes.filter((s): s is Scope => ALL_SCOPES.includes(s as Scope)))
      : DEFAULT_STATE.scopes;
    const includeTest = typeof v.includeTest === "boolean" ? v.includeTest : DEFAULT_STATE.includeTest;
    return { scopes, includeTest };
  } catch {
    return DEFAULT_STATE;
  }
}

async function persistFilter(viewKey: string, state: ViewFilterState): Promise<void> {
  try {
    const csrf = getCsrfToken();
    const r = await fetch(`/api/user/settings/view_filters`, { credentials: "same-origin" });
    let all: Record<string, unknown> = {};
    if (r.ok) {
      const body = (await r.json()) as { value?: Record<string, unknown> };
      all = body.value || {};
    }
    all[viewKey] = state;
    await fetch(`/api/user/settings`, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        ...(csrf ? { "X-CSRF-Token": csrf } : {}),
      },
      body: JSON.stringify({ key: "view_filters", value: all }),
    });
  } catch {
    /* best-effort; the local state still applies for this session */
  }
}

/** Convert a filter state to URL query params for an /api/* call.
 *  Empty scopes array means "everything" so the param is omitted; a
 *  non-default scope set is encoded as repeated `scope=` entries. */
export function filterToParams(state: ViewFilterState): URLSearchParams {
  const p = new URLSearchParams();
  if (state.scopes.length > 0 && state.scopes.length < ALL_SCOPES.length) {
    for (const s of state.scopes) p.append("scope", s);
  }
  if (state.includeTest) p.set("include_test", "1");
  return p;
}

/** React hook: returns the current filter state + setter for a given
 *  view key. Reads the saved state on mount; writes back on every
 *  change. The `onChange` callback fires AFTER the new state is set
 *  + persisted so the calling page can refetch its data. */
export function useViewFilter(
  viewKey: string,
  onChange?: (s: ViewFilterState) => void,
): {
  state: ViewFilterState;
  setState: (next: ViewFilterState) => void;
  loaded: boolean;
} {
  const [state, setLocal] = useState<ViewFilterState>(DEFAULT_STATE);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void readSavedFilter(viewKey).then((s) => {
      if (cancelled) return;
      setLocal(s);
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [viewKey]);
  const setState = (next: ViewFilterState) => {
    setLocal(next);
    void persistFilter(viewKey, next);
    onChange?.(next);
  };
  return { state, setState, loaded };
}

/** Visible filter chip + popover. Place in the corner of a view header.
 *  When the filter is non-default, an orange dot lights up so the user
 *  knows results are filtered. */
export function ViewFilter({
  state,
  onChange,
}: {
  state: ViewFilterState;
  onChange: (next: ViewFilterState) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const isDefault =
    state.scopes.length === DEFAULT_STATE.scopes.length &&
    state.scopes.every((s) => DEFAULT_STATE.scopes.includes(s)) &&
    state.includeTest === DEFAULT_STATE.includeTest;

  const toggleScope = (s: Scope) => {
    const next = state.scopes.includes(s)
      ? state.scopes.filter((x) => x !== s)
      : [...state.scopes, s];
    onChange({ ...state, scopes: next });
  };

  const applyPreset = (preset: "all-non-private" | "work-only" | "personal-only" | "everything") => {
    if (preset === "all-non-private") onChange({ ...state, scopes: [...DEFAULT_STATE.scopes] });
    if (preset === "work-only") onChange({ ...state, scopes: ["work", "business"] });
    if (preset === "personal-only") onChange({ ...state, scopes: ["personal", "family"] });
    if (preset === "everything") onChange({ ...state, scopes: [...ALL_SCOPES] });
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={isDefault ? "Filter view" : "Filter view (active)"}
        aria-haspopup="menu"
        aria-expanded={open}
        title={isDefault ? "Filter view" : "Filter view (active)"}
        className="relative inline-flex items-center gap-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold px-2.5 py-1.5 transition-colors"
      >
        <FilterIcon className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Filter</span>
        {!isDefault && (
          <span
            aria-hidden
            className="absolute top-1 right-1 w-2 h-2 rounded-full bg-orange-500 ring-2 ring-white dark:ring-slate-900"
          />
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-72 rounded-2xl border border-slate-200/70 dark:border-slate-700/60 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md shadow-xl z-30 p-3 space-y-3"
        >
          <div>
            <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 mb-1">
              Privacy scope
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {ALL_SCOPES.map((s) => {
                const on = state.scopes.includes(s);
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleScope(s)}
                    aria-pressed={on}
                    className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors ${
                      on
                        ? "bg-sky-100 dark:bg-sky-900/40 text-sky-800 dark:text-sky-200"
                        : "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
                    }`}
                  >
                    <Check className={`w-3 h-3 ${on ? "opacity-100" : "opacity-0"}`} />
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 mb-1">
              Presets
            </div>
            <div className="flex flex-wrap gap-1.5">
              <PresetChip onClick={() => applyPreset("all-non-private")}>All except private</PresetChip>
              <PresetChip onClick={() => applyPreset("work-only")}>Work only</PresetChip>
              <PresetChip onClick={() => applyPreset("personal-only")}>Personal only</PresetChip>
              <PresetChip onClick={() => applyPreset("everything")}>Everything</PresetChip>
            </div>
          </div>

          <label className="flex items-center justify-between gap-2 rounded-lg bg-slate-100 dark:bg-slate-800 px-3 py-2 cursor-pointer">
            <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">Show test entries</span>
            <input
              type="checkbox"
              checked={state.includeTest}
              onChange={(e) => onChange({ ...state, includeTest: e.target.checked })}
              className="w-4 h-4 accent-orange-500"
            />
          </label>

          <div className="text-[10px] text-slate-400 leading-snug">
            Filters apply to lists that carry the matching columns. People, captures, and Recall threads
            scope to the privacy filter when applicable; the test entries toggle hides rows tagged
            <code className="mx-0.5 text-[10px] font-mono">is_test=1</code>
            on every supporting surface.
          </div>
        </div>
      )}
    </div>
  );
}

function PresetChip({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-[11px] font-semibold px-2.5 py-1 transition-colors"
    >
      {children}
    </button>
  );
}
